import { useState, useEffect, useRef, createContext, useContext, ReactNode } from 'react';
import React from 'react';

interface NetworkGateContextValue {
  isOnline: boolean;
}

const NetworkGateContext = createContext<NetworkGateContextValue>({ isOnline: true });

export function useNetworkGate() {
  return useContext(NetworkGateContext);
}

export function NetworkGateProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(true);
  const isNativeRef = useRef(false);
  const cancelledRef = useRef(false);
  const listenerRef = useRef<{ remove: () => void } | null>(null);
  const appListenerRef = useRef<{ remove: () => void } | null>(null);

  useEffect(() => {
    cancelledRef.current = false;

    async function setup() {
      try {
        const { Capacitor } = await import('@capacitor/core');
        isNativeRef.current = Capacitor.isNativePlatform();
      } catch {
        isNativeRef.current = false;
      }

      if (isNativeRef.current) {
        try {
          const { Network } = await import('@capacitor/network');

          const status = await Network.getStatus();
          if (!cancelledRef.current) setIsOnline(status.connected);

          const handle = await Network.addListener('networkStatusChange', (s) => {
            if (!cancelledRef.current) setIsOnline(s.connected);
          });
          listenerRef.current = handle;

          try {
            const { App } = await import('@capacitor/app');
            const appHandle = await App.addListener('appStateChange', async ({ isActive }) => {
              if (isActive && !cancelledRef.current) {
                try {
                  const fresh = await Network.getStatus();
                  if (!cancelledRef.current) setIsOnline(fresh.connected);
                } catch {}
              }
            });
            appListenerRef.current = appHandle;
          } catch {}
        } catch {}
      } else {
        setIsOnline(navigator.onLine);

        const goOnline = () => { if (!cancelledRef.current) setIsOnline(true); };
        const goOffline = () => { if (!cancelledRef.current) setIsOnline(false); };

        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);

        listenerRef.current = {
          remove: () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
          },
        };
      }
    }

    setup();

    return () => {
      cancelledRef.current = true;
      listenerRef.current?.remove();
      listenerRef.current = null;
      appListenerRef.current?.remove();
      appListenerRef.current = null;
    };
  }, []);

  return React.createElement(
    NetworkGateContext.Provider,
    { value: { isOnline } },
    children
  );
}